import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import { 
  Sun, 
  Moon, 
  LogOut, 
  BarChart3,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Package,
  DollarSign,
  Percent,
  Clock,
  Loader2
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Analytics() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [projections, setProjections] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [trendsRes, projectionsRes, statusRes, performersRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/profit-trends?days=30`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/roi-projection?months=6`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/status-breakdown`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/top-performers?limit=5`, { credentials: 'include' })
      ]);

      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends);
      }

      if (projectionsRes.ok) {
        const data = await projectionsRes.json();
        setProjections(data);
      }

      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatusBreakdown(data.breakdown);
      }

      if (performersRes.ok) {
        const data = await performersRes.json();
        setTopPerformers(data.performers);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Profit') || entry.name.includes('ROI') 
                ? (entry.name.includes('ROI') ? `${entry.value}%` : formatCurrency(entry.value))
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartColors = {
    profit: theme === 'dark' ? '#34D399' : '#10B981',
    realized: theme === 'dark' ? '#FCC419' : '#D4AF37',
    inventory: theme === 'dark' ? '#60A5FA' : '#3B82F6',
    projected: theme === 'dark' ? '#A78BFA' : '#8B5CF6',
    grid: theme === 'dark' ? '#27272A' : '#E4E4E7',
    text: theme === 'dark' ? '#A1A1AA' : '#71717A'
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-gold" />
              <span className="font-heading text-lg font-bold">Profit Analytics</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-gold" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    Avg Profit/Book
                  </div>
                  <div className="text-2xl font-bold font-mono text-profit">
                    {formatCurrency(projections?.avg_profit_per_book || 0)}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Clock className="h-4 w-4" />
                    Avg Days to Sell
                  </div>
                  <div className="text-2xl font-bold font-mono">
                    {projections?.avg_days_to_sell || 0}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Percent className="h-4 w-4" />
                    Sell-Through Rate
                  </div>
                  <div className="text-2xl font-bold font-mono text-gold">
                    {projections?.sell_through_rate || 0}%
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <TrendingUp className="h-4 w-4" />
                    Monthly Growth
                  </div>
                  <div className="text-2xl font-bold font-mono text-profit">
                    +{projections?.monthly_growth_rate || 0}%
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 1 */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Profit Trend Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-profit" />
                    Profit Trends (30 Days)
                  </CardTitle>
                  <CardDescription>Expected vs Realized Profit Over Time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="expectedGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={chartColors.profit} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={chartColors.profit} stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="realizedGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={chartColors.realized} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={chartColors.realized} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10, fill: chartColors.text }}
                          tickFormatter={(value) => value.slice(5)}
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: chartColors.text }}
                          tickFormatter={(value) => `£${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="expected_profit" 
                          name="Expected Profit"
                          stroke={chartColors.profit} 
                          fillOpacity={1}
                          fill="url(#expectedGradient)"
                          strokeWidth={2}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="realized_profit" 
                          name="Realized Profit"
                          stroke={chartColors.realized} 
                          fillOpacity={1}
                          fill="url(#realizedGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* ROI Projection Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-gold" />
                    ROI Projection (6 Months)
                  </CardTitle>
                  <CardDescription>Forecasted Profit & Inventory Growth</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={projections?.projections || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 10, fill: chartColors.text }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fontSize: 10, fill: chartColors.text }}
                          tickFormatter={(value) => `£${value}`}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          tick={{ fontSize: 10, fill: chartColors.text }}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="projected_profit" 
                          name="Projected Profit"
                          stroke={chartColors.projected} 
                          strokeWidth={2}
                          dot={{ fill: chartColors.projected, strokeWidth: 2 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="cumulative_roi" 
                          name="Cumulative ROI"
                          stroke={chartColors.realized} 
                          strokeWidth={2}
                          dot={{ fill: chartColors.realized, strokeWidth: 2 }}
                          strokeDasharray="5 5"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              {/* Inventory Trend */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-500" />
                    Inventory Over Time
                  </CardTitle>
                  <CardDescription>Daily inventory by status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trends.slice(-14)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10, fill: chartColors.text }}
                          tickFormatter={(value) => value.slice(8)}
                        />
                        <YAxis tick={{ fontSize: 10, fill: chartColors.text }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="sourced" name="Sourced" stackId="a" fill="#3B82F6" />
                        <Bar dataKey="prepped" name="Prepped" stackId="a" fill="#8B5CF6" />
                        <Bar dataKey="live" name="Live" stackId="a" fill="#10B981" />
                        <Bar dataKey="sold" name="Sold" stackId="a" fill="#EAB308" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Status Breakdown Pie */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Status Breakdown</CardTitle>
                  <CardDescription>Current inventory by status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {statusBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusBreakdown}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            dataKey="count"
                            nameKey="label"
                            label={({ label, count }) => `${label}: ${count}`}
                            labelLine={false}
                          >
                            {statusBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No acquisitions yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Performers Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-gold" />
                  Top Performers
                </CardTitle>
                <CardDescription>
                  {topPerformers.length > 0 && topPerformers[0].actual_profit 
                    ? 'Best selling books by actual profit' 
                    : 'Highest expected profit books'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {topPerformers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Book</th>
                          <th className="text-right py-2 font-medium">Cost</th>
                          <th className="text-right py-2 font-medium">Profit</th>
                          <th className="text-right py-2 font-medium">ROI</th>
                          <th className="text-left py-2 font-medium">Supplier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPerformers.map((item, index) => (
                          <tr key={index} className="border-b hover:bg-muted/50">
                            <td className="py-2">
                              <div className="font-medium">{item.title}</div>
                              <div className="text-xs text-muted-foreground">{item.isbn}</div>
                            </td>
                            <td className="text-right py-2 font-mono">
                              {formatCurrency(item.purchase_price)}
                            </td>
                            <td className="text-right py-2 font-mono text-profit">
                              {formatCurrency(item.actual_profit || item.expected_profit)}
                            </td>
                            <td className="text-right py-2 font-mono text-gold">
                              {item.roi_percent}%
                            </td>
                            <td className="py-2 text-muted-foreground">
                              {item.supplier}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>No acquisition data yet</p>
                    <p className="text-sm">Create acquisitions to see your top performers</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
